// ✅ src/pages/VehicleV2/BatteryCardReport_AllPrint.jsx
// Updated to match your Battery Card Maintenance Report PRINT table fields exactly
// and map values from your API row keys:
//
// API row sample keys you gave:
// VehicleType, currentdate, fld_BranchName,
// fld_Fitteddate, fld_Vehiclename, fld_vehicle_regno,
// fld_batterycode, fld_batterytype, fld_battery_serialno,
// fld_actuallife, fld_currentlife, fld_perdaycost, alert
//
// Backend used:
//   app.use("/api/batteryCardMaintenanceReport", batteryCardMaintenanceReport);
// Endpoint used:
//   GET /api/batteryCardMaintenanceReport/list

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();

function mapFiltersToParams(filters) {
  // ✅ Pass-through for the backend we built earlier (/list)
  // Adjust only if your listing page filter keys differ
  return {
    branchType: toStr(filters.branchType || filters.branchtype || "HO"),
    selectedBranchId: toStr(filters.selectedBranchId || filters.branch || "Select"),
    loginUserId: toStr(filters.loginUserId || filters.loginuserid || ""),

    fromDate: toStr(filters.fromDate || ""),
    toDate: toStr(filters.toDate || ""),

    vehicleCode: toStr(filters.vehicleCode || ""),
    vehicleName: toStr(filters.vehicleName || ""),
    vehicleRegNo: toStr(filters.vehicleRegNo || filters.vehicleRegno || ""),

    batteryCode: toStr(filters.batteryCode || "Select"),
    batteryType: toStr(filters.batteryType || "Select"),
    batterySerialNo: toStr(filters.batterySerialNo || filters.serialNo || "Select"),

    batteryStatus: toStr(filters.batteryStatus || "Select"),
    vehicleMainTypeIndex: toStr(filters.vehicleMainTypeIndex || "Select"),
    vehicleStatus: toStr(filters.vehicleStatus || "Select"),

    store: toStr(filters.store || "Select"),
  };
}

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDateTime = (v) => {
  if (!v) return "";
  // If backend returns ISO (like 2026-02-07T05:26:11.650Z)
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    // Similar compact display as your screenshot (M/D/YYYY hh:mm:ss AM/PM)
    const date = d.toLocaleDateString("en-US");
    const time = d.toLocaleTimeString("en-US");
    return `${date} ${time}`;
  }
  // If backend already returns formatted string
  return String(v);
};

const fmtDateOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-US");
  return String(v);
};

const BatteryCardMaintenance_AllPrint = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filters passed from listing page
  const filters = useMemo(() => location?.state?.filters || {}, [location]);
  const params = useMemo(() => mapFiltersToParams(filters), [filters]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        const apiRows = res?.data?.rows || [];

        // ✅ Normalize into exactly the fields used in the print table
        const normalized = apiRows.map((r, i) => ({
          sno: i + 1,
          vehicleMainType: toStr(r.VehicleType),
          vehicleName: toStr(r.fld_Vehiclename),
          vehicleRegNo: toStr(r.fld_vehicle_regno),
          batteryCode: toStr(r.fld_batterycode),
          batteryType: toStr(r.fld_batterytype),
          batterySerialNo: toStr(r.fld_battery_serialno),
          fittedDate: fmtDateOnly(r.fld_Fitteddate),
          currentRemoveDate: fmtDateTime(r.currentdate),
          actualLife: toStr(r.fld_actuallife),
          currentLife: toStr(r.fld_currentlife),
          perDayCost: fmtMoney(r.fld_perdaycost),
          // optional:
          branchName: toStr(r.fld_BranchName),
          alert: toStr(r.alert),
        }));

        if (alive) setRows(normalized);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .print-landscape { size: A4 landscape; margin: 0.3in; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top actions (not printed) */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Header Section (keep as you had) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Battery Card Maintenance Report</h2>
        </div>

        {/* Loading / Error (not printed) */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading report...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* ✅ Main Table (UPDATED headers + values) */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "30px" }}>
                  S.No
                </th>

                {/* If you want Branch column like screenshot doesn't show, keep it removed.
                    Uncomment below if you want it.
                */}
                {/* <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Branch
                </th> */}

                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "90px" }}>
                  Vehicle Main Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "120px" }}>
                  Vehicle Name
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "110px" }}>
                  Vehicle Reg No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "110px" }}>
                  Battery Code
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Battery Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "95px" }}>
                  Battery Serial No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "85px" }}>
                  Fitted Date
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "130px" }}>
                  Current/Remove Date
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Actual life
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Current life
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Per Day Cost
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="border border-black px-2 py-2 text-center">
                    No records found for selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>

                    {/* <td className="border border-black px-1 py-1">{r.branchName}</td> */}

                    <td className="border border-black px-1 py-1">{r.vehicleMainType}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleName}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleRegNo}</td>
                    <td className="border border-black px-1 py-1">{r.batteryCode}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.batteryType}</td>
                    <td className="border border-black px-1 py-1">{r.batterySerialNo}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fittedDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.currentRemoveDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.actualLife}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.currentLife}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.perDayCost}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg transition-colors duration-200"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatteryCardMaintenance_AllPrint;
